import { useState } from 'react';
import {
  Button,
  Input,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { ChevronRightIcon, FolderIcon, FoldersIcon, LibraryBigIcon, PlusIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  matchingFolderRows,
  toFolderRows,
  visibleFolderRows,
  type MediaFolder,
} from '@/entities/brand-media';
import { usePermissions } from '@/entities/user';
import {
  CollapsibleSection,
  RAIL_INDENT_PX,
  railRowClass,
  railRowCountClass,
  railRowIndent,
} from '@/shared/ui';

import { FolderMenu } from './FolderMenu';

/**
 * Where in the library you are.
 *
 * Every folder is a filter rather than a location: choosing one narrows the same listing, so nothing
 * navigates and there is no state to lose. That is what lets a host mount this view already scoped
 * without the rail having to know it was.
 */
export const FolderRail = ({
  folders,
  total,
  selectedFolderId,
  search = '',
  onSelect,
  onCreate,
  onCreateChild,
  onRename,
  onDelete,
  isCreating,
}: {
  folders: readonly MediaFolder[];
  total: number;
  /** Undefined is the whole library rather than a folder, which is not the same as unfiled. */
  selectedFolderId?: string;
  /** What the host is filtering the whole rail by. Empty shows every folder. */
  search?: string;
  onSelect: (folderId: string | undefined) => void;
  /** Always at the root. Nesting has its own explicit action, so this one never has to guess. */
  onCreate: (name: string) => void;
  onCreateChild: (parentId: string, name: string) => void;
  onRename: (folderId: string, name: string) => void;
  /** The folder itself: the confirmation names it and counts its subtree, which an id alone cannot. */
  onDelete: (folder: MediaFolder) => void;
  isCreating: boolean;
}) => {
  const { t } = useTranslation('tools');
  const { canUploadBrandAssets } = usePermissions();
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState('');
  const [renaming, setRenaming] = useState<string | undefined>(undefined);
  /** Which folders are collapsed. Absent from the set means expanded, so nothing hides on first load. */
  const [collapsedIds, setCollapsedIds] = useState<Set<string>>(new Set());
  const [creatingUnder, setCreatingUnder] = useState<string | undefined>(undefined);
  const [childName, setChildName] = useState('');
  const [open, setOpen] = useState(true);

  /** A rename to the same words, or to nothing, is not a rename: leave the folder as it is. */
  const commitRename = (folderId: string, value: string, current: string) => {
    const trimmed = value.trim();
    if (trimmed.length > 0 && trimmed !== current) {
      onRename(folderId, trimmed);
    }
    setRenaming(undefined);
  };

  const term = search.trim();
  const matched = matchingFolderRows(toFolderRows(folders), term);
  const rows = visibleFolderRows(matched, collapsedIds);

  // A search unfolds what it needs to: a branch kept only because something under it matched, and the
  // section itself. Hits behind a shut row read as the search having found nothing.
  // Merged into the state when the result changes rather than forced on every render, so a branch can
  // still be shut by hand mid-search, and so clearing the term leaves the rail as it was left.
  const revealKey = [term, ...matched.map((row) => row.folder.id)].join(' ');
  const [syncedReveal, setSyncedReveal] = useState(term.length > 0 ? revealKey : '');
  if (term.length > 0 && revealKey !== syncedReveal) {
    setSyncedReveal(revealKey);
    setOpen(true);
    setCollapsedIds((current) => {
      const next = new Set(current);
      for (const row of matched) {
        next.delete(row.folder.id);
      }
      return next.size === current.size ? current : next;
    });
  }

  const submit = () => {
    const trimmed = name.trim();
    if (trimmed.length > 0) {
      onCreate(trimmed);
    }
    setName('');
    setNaming(false);
  };

  const toggleCollapse = (folderId: string) => {
    setCollapsedIds((current) => {
      const next = new Set(current);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  };

  /** Expands the folder too, in case it was collapsed: the input has to be visible to be typed into. */
  const beginCreatingChild = (folderId: string) => {
    setCreatingUnder(folderId);
    setChildName('');
    setCollapsedIds((current) => {
      if (!current.has(folderId)) {
        return current;
      }
      const next = new Set(current);
      next.delete(folderId);
      return next;
    });
  };

  const submitChild = () => {
    const trimmed = childName.trim();
    if (trimmed.length > 0 && creatingUnder !== undefined) {
      onCreateChild(creatingUnder, trimmed);
    }
    setChildName('');
    setCreatingUnder(undefined);
  };

  const cancelChild = () => {
    setChildName('');
    setCreatingUnder(undefined);
  };

  return (
    // Its own provider rather than the app's: this rail is mounted by more than one host, and a
    // tooltip whose provider is missing throws rather than degrading.
    <TooltipProvider>
      <CollapsibleSection
        title={t('brandMedia.folders')}
        // A rank below the rail's own title, which names both of these sections at once.
        size="sm"
        open={open}
        onOpenChange={setOpen}
        action={
          <Tooltip>
            {/* The span takes the pointer events a disabled button drops, so the reason still shows. */}
            <TooltipTrigger asChild>
              <span className="inline-flex">
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7 shrink-0"
                  disabled={isCreating || !canUploadBrandAssets}
                  aria-label={t('brandMedia.newFolder')}
                  // Opens the section as well: a field naming a row that would join a folded list is
                  // a control with nothing on screen to show for it.
                  onClick={() => {
                    setOpen(true);
                    setNaming(true);
                  }}
                >
                  <PlusIcon className="size-4" />
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>
              {canUploadBrandAssets
                ? t('brandMedia.newFolder')
                : t('brandMediaLibrary.permissions.noPermission')}
            </TooltipContent>
          </Tooltip>
        }
      >
        {/* The rows are their own group so the space under the header is set once, independently of
          how tightly the rows sit against each other. */}
        <div className="flex flex-col gap-1">
          {/* Left out of a search: it is the absence of a folder filter rather than a folder, so it
            matches nothing anybody typed and would sit above the results looking like a hit. */}
          {term.length === 0 && (
            <button
              type="button"
              className={railRowClass(selectedFolderId === undefined)}
              onClick={() => onSelect(undefined)}
            >
              <LibraryBigIcon className="size-4 shrink-0" />
              <span className="truncate">{t('brandMedia.allMedia')}</span>
              <span className={railRowCountClass(selectedFolderId === undefined)}>
                {t('brandMedia.rowCount', { count: total })}
              </span>
            </button>
          )}

          {term.length > 0 && rows.length === 0 && (
            <p className="px-sm text-body-md text-foreground-alt">
              {t('brandMedia.noFoldersMatch', { search: term })}
            </p>
          )}

          {rows.flatMap(({ folder, depth, hasChildren }) => {
            const open = !collapsedIds.has(folder.id);

            const folderRow =
              renaming === folder.id ? (
                <Input
                  key={folder.id}
                  ref={(input) => input?.select()}
                  size="sm"
                  defaultValue={folder.name}
                  style={{ marginLeft: depth * RAIL_INDENT_PX }}
                  onBlur={(event) => commitRename(folder.id, event.target.value, folder.name)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') event.currentTarget.blur();
                    if (event.key === 'Escape') setRenaming(undefined);
                  }}
                />
              ) : (
                // A div rather than a button: the menu inside it is itself a button, and a button holding
                // a button is invalid and stops the inner one receiving clicks in some browsers.
                <div
                  key={folder.id}
                  role="button"
                  tabIndex={0}
                  className={`group ${railRowClass(selectedFolderId === folder.id)}`}
                  style={{ paddingLeft: railRowIndent(depth) }}
                  onClick={() => onSelect(folder.id)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      onSelect(folder.id);
                    }
                  }}
                >
                  {hasChildren ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          aria-label={
                            open
                              ? t('brandMedia.collapseFolder', { name: folder.name })
                              : t('brandMedia.expandFolder', { name: folder.name })
                          }
                          className={`flex size-4 shrink-0 items-center justify-center text-muted-foreground transition-transform duration-fast ease-out ${
                            open ? 'rotate-90' : ''
                          }`}
                          // The row underneath is a filter; toggling what it shows is not choosing it.
                          onClick={(event) => {
                            event.stopPropagation();
                            toggleCollapse(folder.id);
                          }}
                        >
                          <ChevronRightIcon className="size-4" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>
                        {open
                          ? t('brandMedia.collapseFolder', { name: folder.name })
                          : t('brandMedia.expandFolder', { name: folder.name })}
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    // A leaf draws no chevron, but still needs the width one would take, or its name
                    // starts closer to the margin than its siblings' and the indent stops reading as one.
                    <span className="size-4 shrink-0" aria-hidden="true" />
                  )}

                  {/* Stacked when the folder holds folders, the way a tag key is drawn against its
                      values. The chevron beside it says the same thing, but only while the row is
                      expanded is the reason visible; the icon says it whether or not it is open. */}
                  {hasChildren ? (
                    <FoldersIcon className="size-4 shrink-0" />
                  ) : (
                    <FolderIcon className="size-4 shrink-0" />
                  )}
                  <span className="truncate" title={folder.name}>
                    {folder.name}
                  </span>
                  {/* `flex-1` on the count rather than on the name: the count reads as part of the
                    label, so it sits against the name and the slack goes after it, which is also
                    what leaves the menu its own place at the end of the row. */}
                  <span className={railRowCountClass(selectedFolderId === folder.id)}>
                    {t('brandMedia.rowCount', { count: folder.subtreeCount })}
                  </span>
                  <FolderMenu
                    folderName={folder.name}
                    onRename={() => setRenaming(folder.id)}
                    onNewSubfolder={() => beginCreatingChild(folder.id)}
                    onDelete={() => onDelete(folder)}
                  />
                </div>
              );

            if (creatingUnder !== folder.id) {
              return [folderRow];
            }

            return [
              folderRow,
              <Input
                key={`${folder.id}-new-child`}
                ref={(input) => input?.focus()}
                size="sm"
                value={childName}
                placeholder={t('brandMedia.folderName')}
                style={{ marginLeft: (depth + 1) * RAIL_INDENT_PX }}
                onChange={(event) => setChildName(event.target.value)}
                onBlur={submitChild}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') event.currentTarget.blur();
                  if (event.key === 'Escape') cancelChild();
                }}
              />,
            ];
          })}

          {/* At the end of the root list rather than under the header that opens it: a folder created
          here joins this list, so the field stands where the row it becomes will. */}
          {naming && (
            <Input
              // Focused through a ref rather than `autoFocus`: this input appears in response to a
              // click, so moving focus to it is expected, where a page that grabs focus on load is not.
              ref={(input) => input?.focus()}
              size="sm"
              value={name}
              placeholder={t('brandMedia.folderName')}
              onChange={(event) => setName(event.target.value)}
              onBlur={submit}
              onKeyDown={(event) => {
                if (event.key === 'Enter') submit();
                if (event.key === 'Escape') {
                  setName('');
                  setNaming(false);
                }
              }}
            />
          )}
        </div>
      </CollapsibleSection>
    </TooltipProvider>
  );
};
